import { Checkbox, TextInput } from '@app/components';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { StyledForms } from '@app/components/styled/form/Form';
import { FormSection } from '@app/components/styled/form/FormSection';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { theme } from '@app/theme';
import * as R from 'remeda';
import { explodePermission, permissions } from '@tickeat/permissions';
import { AppPath } from '@tickeat/types';

type PermissionPath<T = keyof typeof permissions> = T extends `${string}::${infer P}.${string}`
  ? P
  : never;

type PermissionConfiguration = {
  [P in PermissionPath]: string;
};

const permissionConfiguration: PermissionConfiguration = {
  auth: 'Authentification',
  'delivery-app': 'Application livreur',
  roles: 'Rôles',
  users: 'Utilisateurs',
  shops: 'Restaurants',
  'delivery-zones': 'Zones de livraison',
  products: 'Produits',
  customers: 'Clients',
  advertisements: 'Publicités',
  'account-codes': 'Codes comptables',
  catalogs: 'Catalogues',
  options: 'Options',
  promotions: 'Promotions',
  packs: 'Menus',
  'catalog-snapshots': 'Historique des publications de catalogues',
  tags: 'Catégories',
  taxes: 'Taxes',
  'kitchen-areas': 'Lieux de fabrication',
  connectors: 'Connecteurs',
  ingredients: 'Ingrédients',
  loyalties: 'Programme fidélité',
  'order-cancel-reasons': "Raisons d'annulations",
  services: 'Services',
  'payment-methods': 'Moyens de paiement',
  'payment-providers': 'Prestataires de paiement',
  steps: 'Etapes',
  variations: 'Variations',
  timesheets: 'Pointages',
  organizations: 'Organisation',
  suppliers: 'Fournisseurs',
  'supplier-articles': 'Articles fournisseurs',
  'ingredient-mappings': 'Rattachements ingrédients',
  recipes: 'Fiches techniques',
  'document-imports': 'Documents fournisseur',
  invoices: 'Factures fournisseur',
  'ai-credits': 'Crédits IA',

  // Regroupe les permissions fiscales du dashboard ET de la caisse : elles se
  // lisent comme une famille, et leurs libellés disent lequel des deux
  // logiciels elles ouvrent.
  fiscal: 'Conformité fiscale',

  // Namespace history:: — regroupées sous un intitulé commun pour qu'elles
  // se lisent comme une famille dans la liste des permissions.
  days: 'Historique — Journées',
  orders: 'Historique — Commandes',
  search: 'Historique — Recherche',
  anomalies: 'Historique — Anomalies',
  reports: 'Historique — Rapports',
};

// history::anomalies.read donne accès à des montants imputés nommément à
// des salariés, affichés à leur employeur. On l'affiche à part avec un
// avertissement explicite pour qu'elle ne soit jamais cochée par réflexe
// en même temps que le reste du groupe "historique".
const permissionGroupNotes: Partial<Record<PermissionPath, string>> = {
  anomalies: 'Donne accès à des montants imputés nommément à des salariés.',
};

const groupedPermissions = R.pipe(
  R.entries(permissions),
  R.filter(([permission]) => {
    const path = explodePermission(permission).path;
    return !!permissionConfiguration[path];
  }),
  R.groupBy(([permission]) => explodePermission(permission).path),
  R.mapValues((items, path) => ({
    path,
    description: permissionConfiguration[path],
    note: permissionGroupNotes[path],
    permissions: items.map(([permission, description]) => ({
      permission: permission.replace('.', '_'),
      description,
    })),
  })),
  R.values(),
);

type OpenGroups = Partial<Record<AppPath, boolean>>;

export const RoleForm = ({ defaultOpenGroups }: { defaultOpenGroups?: OpenGroups }) => {
  const [openGroups, setOpenGroups] = useState<OpenGroups>(defaultOpenGroups ?? ({} as OpenGroups));

  const toggleGroup = (path: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  return (
    <StyledForms.Form>
      <FormSection.Section>
        <FormSection.Title>Informations générales</FormSection.Title>
        <FormSection.Content>
          <TextInput name="name" label="Nom" autoCapitalize="none" />

          <PermissionsContainer>
            <PermissionsGroup>
              <PermissionsGroupTitle>Permissions</PermissionsGroupTitle>
              <PermissionsGroupContent>
                {groupedPermissions.map((group) => (
                  <DropdownPermissionGroup
                    key={group.path}
                    group={group}
                    open={!!openGroups[group.path]}
                    onToggle={() => toggleGroup(group.path)}
                  />
                ))}
              </PermissionsGroupContent>
            </PermissionsGroup>
          </PermissionsContainer>
        </FormSection.Content>
      </FormSection.Section>
    </StyledForms.Form>
  );
};

type DropdownPermissionGroupProps = {
  group: {
    path: string;
    description: string;
    note?: string;
    permissions: { permission: string; description: string }[];
  };
  open: boolean;
  onToggle: () => void;
};

const DropdownPermissionGroup: React.FC<DropdownPermissionGroupProps> = ({
  group,
  open,
  onToggle,
}) => {
  const formMethods = useFormContext();

  const formPermissions = formMethods.watch('permissions');

  return (
    <DropdownContainer>
      <DropdownHeader onPress={onToggle} activeOpacity={0.88}>
        <DropdownHeaderTitles>
          <DropdownTitle>{group.description}</DropdownTitle>
          {!!group.note && <DropdownNote>{group.note}</DropdownNote>}
        </DropdownHeaderTitles>
        <DropdownChevron>{open ? '▲' : '▼'}</DropdownChevron>
      </DropdownHeader>
      {open && (
        <PermissionsSubGroup>
          <PermissionsSubGroupContent>
            {group.permissions.map(({ permission, description }) => {
              const [gateway, rest] = permission.split('::');
              const [path] = rest.split('_');

              const wildcardPermission = `${gateway}::${path}_*`;

              const isDisabled =
                !!formPermissions[wildcardPermission] && wildcardPermission !== permission;

              return (
                <PermissionsGroupItem key={permission} disabled={isDisabled}>
                  <Checkbox
                    name={`permissions.${permission}`}
                    disabled={isDisabled}
                    label={description}
                  />
                </PermissionsGroupItem>
              );
            })}
          </PermissionsSubGroupContent>
        </PermissionsSubGroup>
      )}
    </DropdownContainer>
  );
};

const PermissionsContainer = styled.View({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: normalize(16), // entre le plus petit et l'original
});

const PermissionsGroup = styled.View({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: normalize(18),
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(8),
  padding: normalize(20),
  shadowColor: theme.colors.oxfordblue,
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: normalize(5),
  elevation: 1,
});

const PermissionsGroupTitle = styled.Text({
  fontSize: normalize(21),
  paddingVertical: normalize(12),
  fontWeight: '700',
  color: theme.colors.babypowder,
  letterSpacing: 0.2,
});

const PermissionsGroupContent = styled.View({
  display: 'flex',
  flexDirection: 'column',
});

const PermissionsGroupItem = styled.View<{ hasSubGroup?: boolean; disabled?: boolean }>(
  ({ props }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: props.hasSubGroup ? 0 : 1,
    borderBottomColor: theme.colors.grey,
    borderStyle: 'dashed',
    opacity: props.disabled ? 0.5 : 1,
    minHeight: normalize(32),
    paddingVertical: normalize(8),
  }),
);

const PermissionsSubGroup = styled.View({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingLeft: normalize(16),
  borderTopWidth: 1,
  borderTopColor: theme.colors.grey,
  borderStyle: 'dashed',
});

const PermissionsSubGroupContent = styled.View({
  display: 'flex',
  flexDirection: 'column',
  rowGap: normalize(4),
});

// Dropdown styles un peu plus gros que la version réduite, mais plus petit que l'original
const DropdownContainer = styled.View({
  width: '100%',
  marginBottom: normalize(9),
});

const DropdownHeader = styled.TouchableOpacity({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.colors.translucid,
  paddingVertical: normalize(14),
  paddingHorizontal: normalize(12),
  borderRadius: normalize(5),
  marginBottom: normalize(4),
  marginTop: normalize(4),
  shadowColor: theme.colors.oxfordblue,
  shadowOffset: { width: 0, height: 1 },
  shadowOpacity: 0.08,
  shadowRadius: normalize(3),
  elevation: 1,
});

const DropdownHeaderTitles = styled.View({
  display: 'flex',
  flexDirection: 'column',
  rowGap: normalize(4),
  flexShrink: 1,
});

const DropdownTitle = styled.Text({
  fontWeight: '700',
  fontSize: normalize(17),
  color: theme.colors.babypowder,
  letterSpacing: 0.3,
});

const DropdownNote = styled.Text({
  fontWeight: '600',
  fontSize: normalize(13),
  color: theme.colors.gold,
  letterSpacing: 0.2,
});

const DropdownChevron = styled.Text({
  marginLeft: normalize(9),
  fontSize: normalize(16),
  color: theme.colors.babypowder,
  alignSelf: 'center',
});
